import React, { useState } from 'react';
import { APP_TIMEZONE } from '../config';

const TicketCard = ({ ticket, defaultExpanded = false, ticketRef, user, onFollowToggle }) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [followLoading, setFollowLoading] = useState(false);

  const isFollowing = user?.followedTickets?.includes(ticket._id) || false;
  const canFollow = user && ticket.status !== 'closed';

  const handleFollowClick = async (e) => {
    e.stopPropagation();
    if (!onFollowToggle || followLoading) return;
    setFollowLoading(true);
    try {
      await onFollowToggle(ticket._id, isFollowing);
    } finally {
      setFollowLoading(false);
    }
  };

  const priorityColors = {
    urgent: 'bg-red-100 border-red-400 text-red-800',
    high: 'bg-orange-100 border-orange-400 text-orange-800',
    medium: 'bg-yellow-100 border-yellow-400 text-yellow-800',
    low: 'bg-green-100 border-green-400 text-green-800'
  };

  const statusColors = {
    open: 'bg-blue-500 text-white',
    closed: 'bg-gray-500 text-white'
  };

  const priorityBadgeColor = priorityColors[ticket.priority] || priorityColors.medium;
  const statusBadgeColor = statusColors[ticket.status] || statusColors.open;

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: APP_TIMEZONE
    });
  };

  const latestUpdate = ticket.updates && ticket.updates.length > 0
    ? [...ticket.updates].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))[0]
    : { timestamp: ticket.createdAt, content: null };

  return (
    <div
      ref={ticketRef}
      className={`border-2 rounded-lg p-4 cursor-pointer transition-all hover:shadow-lg ${priorityBadgeColor}`}
      onClick={() => setIsExpanded(!isExpanded)}
    >
      <div className="flex flex-col sm:flex-row justify-between items-start gap-2 mb-2">
        <h3 className="text-lg font-semibold flex-1 min-w-0 break-words">
          {ticket.ticketNumber && <span className="text-primary">{ticket.ticketNumber}: </span>}
          {ticket.title}
        </h3>
        <div className="flex flex-wrap gap-2 items-center">
          {canFollow && (
            <button
              onClick={handleFollowClick}
              disabled={followLoading}
              className={`px-3 py-1 rounded border text-xs font-semibold transition-colors ${
                isFollowing
                  ? 'border-primary bg-primary/10 text-primary hover:bg-primary/20'
                  : 'border-primary bg-primary text-primary-foreground hover:bg-primary/90'
              } disabled:opacity-50`}
              title={isFollowing ? 'Unfollow this ticket' : 'Follow this ticket for updates'}
            >
              {isFollowing ? 'Following' : 'Follow'}
            </button>
          )}
          <span className={`px-2 py-1 rounded text-xs font-semibold uppercase ${statusBadgeColor}`}>
            {ticket.status}
          </span>
          <span className={`px-2 py-1 rounded text-xs font-semibold uppercase border ${priorityBadgeColor}`}>
            {ticket.priority}
          </span>
        </div>
      </div>

      {!isExpanded && (
        <>
          <p className="text-sm text-gray-800 mb-2 line-clamp-2">
            {ticket.description}
          </p>
          <div className="text-xs text-gray-700 mt-2">
            {latestUpdate.content && (
              <p className="line-clamp-1">{latestUpdate.content}</p>
            )}
            <p className={latestUpdate.content ? "mt-1" : ""}>Last updated: {formatDate(latestUpdate.timestamp)}</p>
          </div>
          <p className="text-xs mt-2 font-semibold text-gray-700">
            Click to expand {ticket.updates?.length > 0 ? `(${ticket.updates.length} updates)` : ''}
          </p>
        </>
      )}

      {isExpanded && (
        <div className="mt-4 space-y-4">
          <div className="p-3 rounded shadow-sm" style={{ backgroundColor: 'white' }}>
            <p className="text-sm font-semibold mb-1 text-gray-900">Description:</p>
            <p className="text-sm text-gray-800">{ticket.description}</p>
          </div>

          {ticket.updates && ticket.updates.length > 0 && (
            <div className="p-3 rounded shadow-sm" style={{ backgroundColor: 'white' }}>
              <p className="text-sm font-semibold mb-3 text-gray-900">Update History:</p>
              <div className="space-y-3">
                {[...ticket.updates]
                  .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
                  .map((update, index, arr) => (
                    <div
                      key={index}
                      className={`p-3 ${index !== arr.length - 1 ? 'border-b border-gray-200' : ''}`}
                      style={{ backgroundColor: 'white' }}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <span className="text-xs text-gray-500">
                          {formatDate(update.timestamp)}
                        </span>
                      </div>
                      <p className="text-sm text-gray-800">{update.content}</p>
                    </div>
                  ))}
              </div>
            </div>
          )}

          <div className="text-xs text-gray-600">
            <p>Created: {formatDate(ticket.createdAt)}</p>
            <p>Last Updated: {formatDate(latestUpdate.timestamp)}</p>
          </div>

          <p className="text-xs font-semibold text-gray-700 text-center">
            Click to collapse
          </p>
        </div>
      )}
    </div>
  );
};

export default TicketCard;
